import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { Routes } from '@angular/router';
import { headerItem } from '@standard-angular-utilities/shared-components';
import { SharedFunctionsService } from 'BaseServices/sharedFunctions.service';
import { User, UserDTO } from 'BaseDataTypes';

@Injectable({ providedIn: 'root' })
export class UserService {
  currentUser?: User;
  profilePicture?: string;
  idToken?: string;

  currentRouteConfig: Routes = [];
  routesAndNames: headerItem[] = [];

  constructor(
    private http: HttpClient,
    private authService: SocialAuthService,
    private sharedFunctionsService: SharedFunctionsService
  ) {
    //login automatically with idtoken if possible
    this.idToken = localStorage.getItem('idToken')?.toString();
    this.profilePicture = localStorage.getItem('profilePicture')?.toString();
  }

  /**
   * emits a boolean that indicates that user status has changed with a boolean that indicates whether he has logged in
   */
  private userStatusChanged = new BehaviorSubject<boolean>(false);
  userStatusChanged$ = this.userStatusChanged.asObservable();

  /**
   * emits  boolean to indicate if the user has logged in manually
   */
  private manualLogin = new BehaviorSubject<boolean>(false);
  manualLogin$ = this.manualLogin.asObservable();

  /**
   * tracks the current selected role and emits
   */

  selectedRole = UserRoleType.Unauthorized;

  setSelectedRole(userRole: UserRoleType): void {
    this.selectedRole = userRole;
    this.setRouteNamesAndSites();
    this.userStatusChanged.next(true);
  }
  /**
   * retrieves the current value of the selected role ID
   */
  get getSelectedRole(): UserRoleType {
    return this.selectedRole;
  }

  /**
   * attempts to log in a user automatically by using their ID token
   */
  validateIdTokenAndLoginUser(idToken: string): Observable<User> | undefined {
    if (idToken) {
      var returnSubject = new Subject<User>();
      this.http
        .post<loginResponse>('/api/Login', { IdToken: idToken })
        .subscribe((result) => {
          if (result) {
            if (!this.currentUser) {
              this.setUserVariablesFromLoginResponse(result);
            }
            localStorage.setItem('jwtToken', result.jwtToken); // store JWT token
            returnSubject.next(result.user);
          } else {
            this.signOut();
          }
        });
      return returnSubject.asObservable();
    }
    return undefined;
  }

  /**
   * validates the user's google login by sending the ID token to backend
   */
  validateGoogleLogin(googleLoginResponse: SocialUser) {
    this.profilePicture = googleLoginResponse.photoUrl; // stores the profile picture
    //check with backend if user exists in database and has access.
    this.http
      .post<loginResponse>('/api/Login', {
        IdToken: googleLoginResponse.idToken,
      })
      .subscribe((result) => {
        if (result) {
          this.setUserVariablesFromLoginResponse(result);
          localStorage.setItem('idToken', googleLoginResponse.idToken ?? ''); // store google ID token
          localStorage.setItem(
            'profilePicture',
            googleLoginResponse.photoUrl ?? ''
          ); // stores the profile picture
          this.manualLogin.next(true); // notification of manual login
          return 'Login successful!'; // sucess indication
        } else {
          this.signOut();
          return 'Login unsuccessful, please try again.'; // failure indication
        }
      });
  }

  /**
   * sets different fields from the login response, both from manual login, and from automatic login
   * @param result
   */
  setUserVariablesFromLoginResponse(result: loginResponse) {
    this.currentUser = result.user;
    this.setSelectedRoleBasedOnSite(window.location.href);
    this.userStatusChanged.next(true);
  }

  setSelectedRoleBasedOnSite(url: string) {
    if (!this.currentUser) return;
    if (url.toLocaleLowerCase().includes('kursus')) {
      this.selectedRole = this.currentUser.courseRole;
    } else if (url.toLocaleLowerCase().includes('timelog')) {
      this.selectedRole = this.currentUser.timelogRole;
    } else {
      //default to kursus for now.
      this.selectedRole = this.currentUser.courseRole;
    }
  }

  setRouteConfig(config: Routes) {
    this.currentRouteConfig = config;
    this.setRouteNamesAndSites();
  }
  setRouteNamesAndSites() {
    this.routesAndNames = [];
    this,
      this.currentRouteConfig.forEach((route) => {
        if (
          route.data?.['allowedUserRoleType'].includes(this.getSelectedRole)
        ) {
          if (!route.path) return;
          let nameTitle = this.sharedFunctionsService.capitalize(route.path);
          this.routesAndNames.push({
            route: route.path,
            name: (route?.title as string) ?? nameTitle,
          });
        }
      });
  }

  /**
   * gets a list of all users from the backend
   */
  getUsers(): Observable<User[]> {
    return this.http.get<User[]>('/api/user');
  }
  getUserDTOs(): Observable<UserDTO[]> {
    return this.http.get<UserDTO[]>('/api/user/DTOs');
  }

  /**
   * gets details of a specific user from their ID
   * @param userId - ID of the user
   * @returns - observable that send the user details
   */
  getUser(userId: number): Observable<User> {
    return this.http.get<User>('/api/user/' + userId);
  }

  /**
   * creates a new user
   * @param user - the user object with user details
   * @returns - observable the sends the created user
   */
  createUser(user: User): Observable<User> {
    return this.http.post<User>('/api/user/createUser', user);
  }

  /**
   * deletes a user
   * @param userId - the ID from the user
   * @returns - observable that sends a boolean of success/failure
   */
  deleteUser(userId: number): Observable<boolean> {
    return this.http.delete<boolean>('/api/user/' + userId);
  }

  /**
   * updates user details
   * @param user - the updated user
   * @returns - observable that sends the updated user
   */
  updateUser(user: User): Observable<User> {
    user.nearestManager = undefined;
    return this.http.put<User>('/api/user/updateUser', user);
  }

  /**
   * gets the nearest manager for the user
   * @returns - observable that sends a list of nearest managers
   */
  getNearestManagers(): Observable<User[]> {
    return this.http.get<User[]>('/api/user/nearestManagers');
  }

  /**
   * logs out the user by clearing local data
   */
  signOut(): void {
    this.authService.signOut(true);
    localStorage.removeItem('idToken');
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('profilePicture');
    this.currentUser = undefined;
  }

  get getUserRoleTypes() {
    return UserRoleType;
  }
}

interface loginResponse {
  jwtToken: string;
  user: User;
}

export enum UserRoleType {
  Admin = 'Admin',
  Manager = 'Manager',
  User = 'User',
  Unauthorized = 'Unauthorized',
}

export const UserRoleTypeTranslations: {
  [key in UserRoleType]: string;
} = {
  [UserRoleType.Admin]: 'Administrator',
  [UserRoleType.Manager]: 'Leder',
  [UserRoleType.User]: 'Bruger',
  [UserRoleType.Unauthorized]: 'Uautoriseret',
};
export interface InternalTitle {
  id: number;
  name: string;
}
